import React, { useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Badge } from "primereact/badge";
import { confirmDialog } from "primereact/confirmdialog";

// eslint-disable-next-line
import { Toast } from "primereact/toast";
import {
  handleHardDeleteForm,
  handleSoftDeleteForm,
  handleResetForm,
} from "./api";
import api from "../../Services/api";
import FormPreviewDialog from "./FormPreviewDialog";
import ShareFormDialog from "./ShareFormDialog";
import JsonDialog from "./JsonDialog";
import DeleteFormDialog from "./DeleteFormDialog";
import ResetFormDialog from "./ResetFormDialog";

const FormsListDatatableComponent = ({
  data,
  filters,
  setFilters,
  header,
  loading,
  first,
  rows,
  totalRecords,
  onPageChange,
  handleStatusChange,
  onFormDeleted,
  onSort,
  sortField,
  sortOrder,
  editingUsageRow,
  onEditingUsageRowChange,
  onUsageChange,
}) => {
  const [selectedForm, setSelectedForm] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [shareFormId, setShareFormId] = useState(null);
  const [shareForm, setShareForm] = useState(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteFormId, setDeleteFormId] = useState(null);
  const [jsonDialogVisible, setJsonDialogVisible] = useState(false);
  const [jsonForm, setJsonForm] = useState(null);
  const [resetDialogVisible, setResetDialogVisible] = useState(false);
  const [resetFormId, setResetFormId] = useState(null);
  const [downloadingFormId, setDownloadingFormId] = useState(null);
  const [originalUsage, setOriginalUsage] = useState(null);

  const toast = useRef(null);

  const updateFormUsage = async (formId, usage) => {
    try {
      const response = await api.put(`/forms/toggle-usage/${formId}`, {
        usage,
      });
      return response.data.form;
    } catch (error) {
      console.error("Error updating form usage:", error);
      throw error;
    }
  };
  const handleUsageChange = async (rowData, value) => {
    if (value === originalUsage) {
      onEditingUsageRowChange(null);
      setOriginalUsage(null);

      if (toast.current) {
        toast.current.show({
          severity: "info",
          summary: "Info",
          detail: "The selected value is the same as the current value.",
          life: 3000,
        });
      }
      return;
    }

    try {
      await updateFormUsage(rowData.id, value);

      onUsageChange(rowData.id, value);

      onEditingUsageRowChange(null);
      setOriginalUsage(null);

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `Form usage updated to ${value}`,
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to update form usage:", error);

      onEditingUsageRowChange(null);
      setOriginalUsage(null);

      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to update form usage",
          life: 3000,
        });
      }
    }
  };

  // Usage options for the dropdown
  const usageOptions = [
    { label: "In Development", value: "development" },
    { label: "In Production", value: "production" },
    { label: "In Testing", value: "testing" },
  ];

  const viewForm = (rowData) => {
    setSelectedForm(rowData);
    setDialogVisible(true);
  };

  const viewJson = (rowData) => {
    setJsonForm(rowData);
    setJsonDialogVisible(true);
  };

  const shareFormHandler = (rowData) => {
    setShareFormId(rowData.id);
    setShareForm(rowData);
    setShareDialogVisible(true);
  };

  const exportToSkeyechartHandler = (rowData) => {
    const formId = rowData.id;
    setDownloadingFormId(formId);

    const handleAction = async (behaviorIfExists) => {
      try {
        setDownloadingFormId(formId);

        const response = await api.post("/skeyechart/upload", {
          formId,
          behaviorIfExists: behaviorIfExists,
        });

        // Check for successful HTTP status code (2xx) instead of a success property
        if (response.status >= 200 && response.status < 300) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail:
              response.data.message ||
              "Form data uploaded to Skeyechart successfully",
            life: 3000,
          });
        } else {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              response.data.message || "Failed to upload data to Skeyechart",
            life: 3000,
          });
        }
      } catch (error) {
        console.error(`Error during ${behaviorIfExists} operation:`, error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            error.response?.data?.message ||
            "Failed to upload data to Skeyechart",
          life: 3000,
        });
      } finally {
        setDownloadingFormId(null);
      }
    };

    confirmDialog({
      message: "How would you like to export data to Skeyechart?",
      header: "Export to Skeyechart",
      icon: "pi pi-question-circle",
      acceptLabel: "Replace",
      rejectLabel: "Append",
      accept: () => handleAction("REPLACE"),
      reject: () => handleAction("APPEND"),
      onHide: () => setDownloadingFormId(null),

      acceptClassName: "p-button-outlined",
      rejectClassName: "p-button-outlined",
      acceptStyle: {
        backgroundColor: "#0D2E4D",
        color: "white",
        borderColor: "#0D2E4D",
      },
      rejectStyle: {
        backgroundColor: "#0D2E4D",
        color: "white",
        borderColor: "#0D2E4D",
      },
    });
  };

  const downloadCsv = async (formId, formTitle) => {
    if (!formId) return;

    setDownloadingFormId(formId);
    try {
      // Use axios with responseType blob to get binary data
      const response = await api.get(
        `/filled-forms/formSchemaId/${formId}/download-csv`,
        {
          responseType: "blob",
        }
      );

      // Create a URL for the blob
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = `${formTitle || "filled_forms"}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();

      // Clean up
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "CSV file downloaded successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to download CSV file",
        life: 3000,
      });
    } finally {
      setDownloadingFormId(null);
    }
  };

  const resetFormHandler = (rowData) => {
    setResetFormId(rowData.id);
    setResetDialogVisible(true);
  };

  const confirmResetForm = async () => {
    try {
      await handleResetForm(resetFormId);
      setResetDialogVisible(false);

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Form data has been reset successfully",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to reset form:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to reset form data",
          life: 3000,
        });
      }
    }
  };

  const deleteFormHandler = (rowData) => {
    setDeleteFormId(rowData.id);
    setDeleteDialogVisible(true);
  };

  const confirmDeleteForm = async () => {
    try {
      await handleHardDeleteForm(deleteFormId);
      setDeleteDialogVisible(false);

      // Call the callback to update parent component's state
      if (onFormDeleted) {
        onFormDeleted(deleteFormId, "hard");
      }

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Form permanently deleted",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to delete form:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete form",
          life: 3000,
        });
      }
    }
  };

  const confirmSoftDeleteForm = async () => {
    try {
      await handleSoftDeleteForm(deleteFormId);
      setDeleteDialogVisible(false);

      // Call the callback to update parent component's state
      if (onFormDeleted) {
        onFormDeleted(deleteFormId, "soft");
      }

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Form moved to trash",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to soft delete form:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to move form to trash",
          life: 3000,
        });
      }
    }
  };
  const handleStatusChangeWrapper = async (rowData, value) => {
    try {
      await handleStatusChange(rowData.id, rowData.status, rowData.title);
    } catch (error) {
      console.error("Failed to update form status:", error);
    }
  };

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date);
  };

  return (
    <div className="table-container">
      <Toast ref={toast} />
      {loading ? (
        <div className="loader-container">
          <ProgressSpinner />
        </div>
      ) : (
        <>
          <DataTable
            key={editingUsageRow ?? "forms-table"}
            dataKey="id"
            style={{ fontFamily: "poppins" }}
            value={data}
            paginator={false}
            responsiveLayout="scroll"
            filters={filters}
            globalFilterFields={["id", "title", "createdAt", "createdBy"]}
            header={header}
            emptyMessage="No forms found."
            onFilter={(e) => setFilters(e.filters)}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            <Column
              field="title"
              header="Title"
              style={{ width: "20%" }}
              sortable
            ></Column>
            <Column
              field="createdAt"
              header="Created At"
              body={(rowData) => formatDate(rowData.createdAt)}
              style={{ width: "15%" }}
              sortable
            ></Column>
            <Column
              field="createdBy"
              header="Created By"
              style={{ width: "10%" }}
              sortable
            ></Column>
            <Column
              field="status"
              header="Status"
              body={(rowData) => (
                <InputSwitch
                  checked={rowData.status === "up"}
                  onChange={(e) => handleStatusChangeWrapper(rowData, e.value)}
                />
              )}
              style={{ width: "5%" }}
              sortable
            ></Column>
            <Column
              field="usage"
              header="Usage"
              body={(rowData) => {
                const getUsageBadgeStyle = (usage) => {
                  switch (usage) {
                    case "production":
                      return {
                        background: "#609856",
                        color: "white",
                        fontSize: "0.9rem",
                      };
                    case "development":
                      return {
                        background: "#0D2E4D",
                        color: "white",
                        fontSize: "0.9rem",
                      };
                    default: // testing
                      return {
                        background: "#e29b45",
                        color: "white",
                        fontSize: "0.9rem",
                      };
                  }
                };

                const getUsageLabel = (usage) => {
                  switch (usage) {
                    case "production":
                      return "In Production";
                    case "development":
                      return "In Development";
                    default: // testing
                      return "In Testing";
                  }
                };

                if (editingUsageRow === rowData.id) {
                  return (
                    <div className="flex align-items-center">
                      <Dropdown
                        value={rowData.usage}
                        options={usageOptions}
                        onChange={(e) => handleUsageChange(rowData, e.value)}
                        style={{ width: "100%" }}
                        className="p-column-filter mr-2"
                        autoFocus
                      />
                      <Button
                        icon="pi pi-times"
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={() => {
                          // Cancel edit and revert to badge
                          onEditingUsageRowChange(null);
                          setOriginalUsage(null);
                        }}
                        style={{ padding: "0.2rem", color: "red" }}
                        tooltip="Cancel Edit"
                        tooltipOptions={{ position: "top" }}
                      />
                    </div>
                  );
                } else {
                  return (
                    <div className="flex align-items-center">
                      <Badge
                        value={getUsageLabel(rowData.usage)}
                        severity="info"
                        style={getUsageBadgeStyle(rowData.usage)}
                        className="mr-2"
                      />
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={() => {
                          setOriginalUsage(rowData.usage);
                          onEditingUsageRowChange(rowData.id);
                        }}
                        style={{ padding: "0.2rem" }}
                        tooltip="Edit Usage"
                        tooltipOptions={{ position: "top" }}
                      />
                    </div>
                  );
                }
              }}
              style={{ width: "15%" }}
              sortable
            />
            <Column
              header="Actions"
              body={(rowData) => (
                <div className="actions">
                  <Button
                    icon="pi pi-eye"
                    className="p-button p-button-info mr-2"
                    style={{
                      backgroundColor: "#fff",
                      color: "#0d2e4d",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    tooltip="Form Preview"
                    onClick={() => viewForm(rowData)}
                  />
                  <Button
                    icon="pi pi-code"
                    className="p-button p-button-info mr-2"
                    style={{
                      backgroundColor: "#fff",
                      color: "#0d2e4d",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    tooltip="Form JSON"
                    onClick={() => viewJson(rowData)}
                  />
                  <Button
                    icon="pi pi-share-alt"
                    className="p-button p-button-success mr-2"
                    style={{
                      backgroundColor: "#fff",
                      color: "#d97a07",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    tooltip="Share Form"
                    onClick={() => shareFormHandler(rowData)}
                  />
                  <Button
                    icon="pi pi-file-export"
                    className="p-button p-button-success mr-2"
                    tooltip="Export Form Data To Skeyechart"
                    style={{
                      backgroundColor: "#fff",
                      color: "#d97a07",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    onClick={() => exportToSkeyechartHandler(rowData)}
                  />
                  <Button
                    icon={
                      downloadingFormId === rowData.id
                        ? "pi pi-spin pi-spinner"
                        : "pi pi-download"
                    }
                    className="p-button p-button-info mr-2"
                    style={{
                      backgroundColor: "#fff",
                      color: "#3d6e25",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    onClick={() => downloadCsv(rowData.id, rowData.title)}
                    disabled={downloadingFormId === rowData.id}
                    tooltip="Download Form Data as CSV"
                    tooltipOptions={{ position: "top" }}
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button p-button-danger"
                    style={{
                      backgroundColor: "#fff",
                      color: "#dc3545",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    tooltip="Delete Form"
                    onClick={() => deleteFormHandler(rowData)}
                  />
                  <Button
                    icon="pi pi-eraser"
                    className="p-button p-button-warning"
                    style={{
                      backgroundColor: "#fff",
                      color: "#d97a07",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    tooltip="Reset Form"
                    onClick={() => resetFormHandler(rowData)}
                  />
                </div>
              )}
              style={{ width: "25%" }}
            ></Column>
          </DataTable>
          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
          ></Paginator>
        </>
      )}
      <FormPreviewDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        form={selectedForm}
      />
      <ShareFormDialog
        visible={shareDialogVisible}
        onHide={() => setShareDialogVisible(false)}
        form={shareForm}
        formId={shareFormId}
      />
      <JsonDialog
        visible={jsonDialogVisible}
        onHide={() => setJsonDialogVisible(false)}
        jsonForm={jsonForm}
      />
      <DeleteFormDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirmDelete={confirmDeleteForm}
        onConfirmSoftDelete={confirmSoftDeleteForm}
        formId={deleteFormId}
      />

      <ResetFormDialog
        visible={resetDialogVisible}
        onHide={() => setResetDialogVisible(false)}
        onConfirmReset={confirmResetForm}
        formId={resetFormId}
      />
    </div>
  );
};

export default FormsListDatatableComponent;
